import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Link2, ListVideo, UploadCloud, Download, Plus, Trash2, Loader2, Check,
  ArrowLeft, ArrowRight, Settings2, Sparkles, ArrowUp, ArrowDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = [
  { id: "url_single", title: "URL Única para Filmes e Séries", badge: "Padrão", Icon: Link2,
    tags: ["Mais Confiável", "Sincronização Automática"], desc: "Uma URL que contenha filmes e séries. Lê direto da fonte." },
  { id: "url_separate", title: "URLs Separadas para Filmes e Séries", Icon: ListVideo,
    tags: ["Mais Confiável", "Sincronização Automática"], desc: "URLs diferentes para filmes e para séries." },
  { id: "upload", title: "Enviar Arquivo M3U", beta: true, Icon: UploadCloud,
    tags: ["Mais Rápido", "Utiliza TMDB"], desc: "Envie seu arquivo M3U direto do computador." },
  { id: "url_download", title: "URL de Download M3U", beta: true, Icon: Download,
    tags: ["Mais Rápido", "Sincronização Automática"], desc: "Uma URL direta para baixar seu arquivo M3U." },
];

const SETTINGS = [
  { key: "sync_movies", label: "Sincronizar somente Filmes",
    desc: "Marque para sincronizar APENAS os filmes. Se deixar filmes e séries desmarcados, os dois são sincronizados.", def: false },
  { key: "sync_series", label: "Sincronizar somente Séries",
    desc: "Marque para sincronizar APENAS as séries. Se marcar filmes e séries, os dois são sincronizados.", def: false },
  { key: "sync_simultaneous", label: "Sincronizar Filmes e Séries ao mesmo tempo",
    desc: "Vale quando os dois estão sendo sincronizados. Desmarque caso o seu MAIN esteja sobrecarregado (aí sincroniza filmes e depois séries).", def: true },
  { key: "use_cache", label: "Usar cache",
    desc: "Deixa a inserção muito mais rápida. Com o cache, atualizações do seu fornecedor podem levar até 12h para chegar.", def: true },
  { key: "add_delay", label: "Adicionar 1 segundo de delay ao ler a lista",
    desc: "Marque caso o script não consiga ler os dados ou seu fornecedor bloqueie múltiplas requisições. Deixa mais lento.", def: false },
];

export default function Step3Source({ code, data, onBack, onNext }) {
  const t = useT();
  const cfg = data?.m3u_config || {};
  const opts = data?.sync_config || {};
  const mergeOn = !!(opts?.movies?.options?.merge_sources || opts?.series?.options?.merge_sources);
  const verifyOn = !!opts?.verify?.enabled;

  const [method, setMethod] = useState(cfg.method || "url_single");
  const [singleUrls, setSingleUrls] = useState(cfg.sources?.length ? cfg.sources : [""]);
  const [downloadUrls, setDownloadUrls] = useState(cfg.sources?.length ? cfg.sources : [""]);
  const [pairs, setPairs] = useState(
    (cfg.sources_movies?.length || cfg.sources_series?.length)
      ? (cfg.sources_movies || []).map((m, i) => ({ movies: m || "", series: (cfg.sources_series || [])[i] || "" }))
      : [{ movies: "", series: "" }]
  );
  const [uploads, setUploads] = useState(cfg.uploads || []);
  const [settings, setSettings] = useState(() => {
    const base = {}; SETTINGS.forEach((s) => (base[s.key] = s.def));
    return { ...base, ...(cfg.settings || {}) };
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setArr = (list, setList) => (i, v) => setList(list.map((x, idx) => (idx === i ? v : x)));
  const addArr = (list, setList, empty) => () => setList([...list, empty]);
  const delArr = (list, setList) => (i) => setList(list.length > 1 ? list.filter((_, idx) => idx !== i) : list);
  const moveArr = (list, setList) => (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setList(copy);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data: res } = await axios.post(`${API}/panel/${code}/m3u-upload`, fd);
        setUploads((u) => [...u, { filename: res.filename, size: res.size }]);
      }
      toast.success("Arquivo(s) enviado(s)!");
    } catch (_) { toast.error("Falha no envio do arquivo."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    const body = { method, settings, sources: [], sources_movies: [], sources_series: [], uploads: [] };
    if (method === "url_single") body.sources = singleUrls.filter(Boolean);
    else if (method === "url_download") body.sources = downloadUrls.filter(Boolean);
    else if (method === "url_separate") {
      body.sources_movies = pairs.map((p) => p.movies).filter(Boolean);
      body.sources_series = pairs.map((p) => p.series).filter(Boolean);
    } else if (method === "upload") body.uploads = uploads;

    const hasSource = body.sources.length || body.sources_movies.length || body.sources_series.length || body.uploads.length;
    if (!hasSource) { toast.error("Informe pelo menos uma fonte M3U."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/panel/${code}/m3u-config`, body);
      toast.success("Fonte M3U salva! Vamos para as categorias.");
      onNext && onNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar a fonte M3U.");
    } finally { setSaving(false); }
  };

  const UrlList = ({ list, setList, placeholder, testid }) => (
    <div className="space-y-2">
      {verifyOn && list.length > 1 && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-700" data-testid={`${testid}-priority-hint`}>
          🔀 <b>Verificador ligado:</b> a ordem abaixo é a <b>prioridade</b>. O 1º é o <b>principal</b>; os seguintes são <b>reservas</b> usados quando um link cair. Use as setas para reordenar.
        </div>
      )}
      {list.map((url, i) => (
        <div key={i} className="flex items-center gap-2">
          {verifyOn && list.length > 1 && (
            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${i === 0 ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-600"}`}>
              {i === 0 ? "PRINCIPAL" : `RESERVA ${i}`}
            </span>
          )}
          <Input data-testid={`${testid}-${i}`} value={url} placeholder={i === 0 ? placeholder : `Fornecedor ${i + 1}`}
            onChange={(e) => setArr(list, setList)(i, e.target.value)} className="font-mono-code text-xs" />
          {list.length > 1 && (
            <>
              <Button size="icon" variant="outline" data-testid={`${testid}-up-${i}`} disabled={i === 0}
                onClick={() => moveArr(list, setList)(i, -1)}><ArrowUp className="h-4 w-4 text-slate-500" /></Button>
              <Button size="icon" variant="outline" data-testid={`${testid}-down-${i}`} disabled={i === list.length - 1}
                onClick={() => moveArr(list, setList)(i, 1)}><ArrowDown className="h-4 w-4 text-slate-500" /></Button>
              <Button size="icon" variant="outline" data-testid={`${testid}-del-${i}`} onClick={() => delArr(list, setList)(i)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </>
          )}
        </div>
      ))}
      <Button size="sm" variant="outline" data-testid={`${testid}-add`} onClick={addArr(list, setList, "")}
        className="text-green-700">
        <Plus className="mr-1.5 h-4 w-4" /> Adicionar outro fornecedor
      </Button>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="mt-6 space-y-6" data-testid="panel-step3">

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="bg-green-600 px-5 py-3 font-display text-lg font-bold text-white">{t("steps.s3how")}</div>
        <div className="space-y-3 p-5">
          {METHODS.map((m) => (
            <button key={m.id} type="button" onClick={() => setMethod(m.id)} data-testid={`m3u-method-${m.id}`}
              className={`flex w-full items-start gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                method === m.id ? "border-green-500 bg-green-50/60" : "border-slate-200 bg-white hover:border-green-300"
              }`}>
              <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 ${method === m.id ? "border-green-600 bg-green-600" : "border-slate-300"}`}>
                {method === m.id && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
              <m.Icon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
              <span className="flex-1">
                <span className="flex flex-wrap items-center gap-2 font-display font-bold text-slate-800">
                  {m.title}
                  {m.badge && <span className="rounded bg-green-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">{m.badge}</span>}
                  {m.beta && <span className="rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">Beta</span>}
                </span>
                <span className="mt-1 block text-xs text-slate-500">{m.desc}</span>
                <span className="mt-1.5 flex flex-wrap gap-1.5">
                  {m.tags.map((t) => <span key={t} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{t}</span>)}
                </span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="bg-slate-800 px-5 py-3 font-display text-lg font-bold text-white">{t("steps.s3source")}</div>
        <div className="space-y-3 p-5" data-testid="m3u-inputs">
          {mergeOn && <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">Quer usar mais de um fornecedor? Adicione o link do 1º, 2º, 3º… clicando em "Adicionar outro fornecedor". O 1º sobe tudo; os seguintes inserem só os filmes que faltam e completam as séries com as temporadas/episódios que faltam. Nada é duplicado e nada é guardado como reserva.</div>}
          {method === "url_single" && <UrlList list={singleUrls} setList={setSingleUrls} testid="m3u-single"
            placeholder="http://dns.com/get.php?username=USER&password=PASS&type=m3u_plus&output=ts" />}
          {method === "url_download" && <UrlList list={downloadUrls} setList={setDownloadUrls} testid="m3u-download"
            placeholder="http://dns.com/arquivo.m3u" />}
          {method === "url_separate" && (
            <div className="space-y-3">
              {pairs.map((p, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-500">Fornecedor {i + 1}</p>
                  <Input data-testid={`m3u-sep-movies-${i}`} value={p.movies} placeholder="URL dos FILMES"
                    onChange={(e) => setPairs(pairs.map((x, idx) => idx === i ? { ...x, movies: e.target.value } : x))} className="font-mono-code text-xs" />
                  <Input data-testid={`m3u-sep-series-${i}`} value={p.series} placeholder="URL das SÉRIES"
                    onChange={(e) => setPairs(pairs.map((x, idx) => idx === i ? { ...x, series: e.target.value } : x))} className="mt-2 font-mono-code text-xs" />
                  {pairs.length > 1 && (
                    <Button size="sm" variant="outline" onClick={() => setPairs(pairs.filter((_, idx) => idx !== i))} className="mt-2 text-red-500">
                      <Trash2 className="mr-1.5 h-4 w-4" /> Remover
                    </Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" data-testid="m3u-sep-add" onClick={() => setPairs([...pairs, { movies: "", series: "" }])} className="text-green-700">
                <Plus className="mr-1.5 h-4 w-4" /> Adicionar outro fornecedor
              </Button>
            </div>
          )}
          {method === "upload" && (
            <div>
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center hover:border-green-400">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin text-green-600" /> : <UploadCloud className="h-6 w-6 text-green-600" />}
                <span className="text-sm text-slate-600">Clique para enviar seu arquivo .m3u{mergeOn ? " (pode enviar de vários fornecedores)" : ""}</span>
                <input type="file" accept=".m3u,.m3u8,.txt" multiple={mergeOn} className="hidden" data-testid="m3u-file-input" onChange={handleFiles} />
              </label>
              {uploads.map((u, i) => (
                <div key={i} className="mt-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 text-slate-700"><Check className="h-4 w-4 text-green-600" /> {u.filename} <span className="text-xs text-slate-400">({Math.round((u.size || 0) / 1024)} KB)</span></span>
                  <Button size="icon" variant="ghost" onClick={() => setUploads(uploads.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-sky-500 px-5 py-3 font-display text-lg font-bold text-white"><Settings2 className="h-5 w-5" /> {t("steps.s3settings")}</div>
        <div className="space-y-2 p-5">
          {SETTINGS.map((s) => (
            <button key={s.key} type="button" data-testid={`m3u-set-${s.key}`} onClick={() => setSettings({ ...settings, [s.key]: !settings[s.key] })}
              className="flex w-full items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-green-300">
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${settings[s.key] ? "border-green-600 bg-green-600 text-white" : "border-slate-300"}`}>
                {settings[s.key] && <Check className="h-3.5 w-3.5" />}
              </span>
              <span>
                <span className="block text-sm font-medium text-slate-800">{s.label}</span>
                <span className="mt-0.5 block text-xs text-slate-500">{s.desc}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button variant="outline" data-testid="step3-back" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}</Button>
        <Button data-testid="step3-save" onClick={save} disabled={saving || uploading}
          className="bg-green-600 py-6 text-base font-semibold text-white hover:bg-green-700 sm:py-2">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {t("common.saveAdvance")} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
}
